import { create } from 'zustand';

import { wallsApi, wallStorage } from '@walls/services/wallsApi';
import type { ParticipantId, GroupId, WallBoard, WallId, WallParticipant, WallGroup } from './types';

interface WallParticipantsState {
  participants: WallParticipant[];
  groups: WallGroup[];
  currentParticipantId: ParticipantId | null;
  joinName: string;
  joinError: string | null;
  pendingRejoinName: string | null;
  isJoining: boolean;
}

interface WallParticipantsActions {
  addParticipant: (participant: WallParticipant) => void;
  removeParticipant: (participantId: ParticipantId) => void;
  upsertGroup: (group: WallGroup) => void;
  removeGroup: (groupId: GroupId) => void;
  hydrateFromBoard: (board: WallBoard) => void;
  setJoinName: (joinName: string) => void;
  joinBoard: (
    board: WallBoard,
    options?: { inviteId?: string | null; topicKey?: string | null }
  ) => Promise<void>;
  deleteParticipant: (
    boardId: WallId,
    participantId: ParticipantId,
    topicKey?: string | null
  ) => Promise<void>;
  createGroup: (
    boardId: WallId,
    name: string,
    participantIds?: ParticipantId[],
    topicKey?: string | null
  ) => Promise<void>;
  renameGroup: (
    boardId: WallId,
    groupId: GroupId,
    name: string,
    topicKey?: string | null
  ) => Promise<void>;
  updateGroup: (
    boardId: WallId,
    group: WallGroup,
    name: string,
    participantIds: ParticipantId[],
    topicKey?: string | null
  ) => Promise<void>;
  deleteGroup: (boardId: WallId, groupId: GroupId, topicKey?: string | null) => Promise<void>;
  setGroupParticipants: (
    boardId: WallId,
    groupId: GroupId,
    participantIds: ParticipantId[],
    topicKey?: string | null
  ) => Promise<void>;
  setParticipantLive: (participantId: ParticipantId) => void;
  setParticipantGone: (participantId: ParticipantId) => void;
  resetParticipants: () => void;
}

export type WallParticipantsStore = WallParticipantsState & WallParticipantsActions;

const sortedParticipants = (participants: WallParticipant[]) =>
  [...participants].sort((a, b) => {
    if (a.isLive !== b.isLive) {
      return a.isLive ? -1 : 1;
    }

    return b.name.toLowerCase().localeCompare(a.name.toLowerCase());
  });

const sortedGroups = (groups: WallGroup[]) =>
  [...groups].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

const initialState: WallParticipantsState = {
  participants: [],
  groups: [],
  currentParticipantId: null,
  joinName: '',
  joinError: null,
  pendingRejoinName: null,
  isJoining: false,
};

export const useWallParticipantsStore = create<WallParticipantsStore>((set, get) => ({
  ...initialState,

  addParticipant: (participant) =>
    set((state) => ({
      participants: sortedParticipants([
        ...state.participants.filter((candidate) => candidate.id !== participant.id),
        participant,
      ]),
    })),

  removeParticipant: (participantId) =>
    set((state) => ({
      participants: state.participants.filter((participant) => participant.id !== participantId),
      groups: sortedGroups(
        state.groups.map((group) => ({
          ...group,
          participantIds: group.participantIds.filter((id) => id !== participantId),
        }))
      ),
      currentParticipantId:
        state.currentParticipantId === participantId ? null : state.currentParticipantId,
    })),

  upsertGroup: (group) =>
    set((state) => ({
      groups: sortedGroups([...state.groups.filter((candidate) => candidate.id !== group.id), group]),
    })),

  removeGroup: (groupId) =>
    set((state) => ({
      groups: state.groups.filter((group) => group.id !== groupId),
    })),

  hydrateFromBoard: (board) => {
    const ownerParticipantId = board.isOwner ? getOwnerParticipantId(board) : null;
    const storedParticipantId = wallStorage.getTopicUser(board.id);
    const storedParticipantExists = Boolean(
      storedParticipantId &&
        board.participants.some((participant) => participant.id === storedParticipantId)
    );
    const currentParticipantId = storedParticipantExists
      ? storedParticipantId
      : ownerParticipantId;
    const storedName = wallStorage.getUserName();
    const participants = board.participants.map((participant) => ({
      ...participant,
      isLive: participant.id === currentParticipantId ? true : participant.isLive ?? false,
    }));

    if (storedParticipantId && !storedParticipantExists) {
      wallStorage.clearTopicUser(board.id);
    }

    if (ownerParticipantId && currentParticipantId === ownerParticipantId) {
      wallStorage.setTopicUser(board.id, ownerParticipantId);
    }

    set({
      participants: sortedParticipants(participants),
      groups: sortedGroups(board.groups ?? []),
      currentParticipantId,
      joinName: currentParticipantId ? '' : storedName ?? '',
      joinError: null,
      pendingRejoinName: null,
    });
  },

  setJoinName: (joinName) => set({ joinName, joinError: null }),

  joinBoard: async (board, options) => {
    const joinName = get().joinName.trim();

    if (!joinName) {
      set({ joinError: 'Required' });
      return;
    }

    if (board.ownerName?.trim().toLowerCase() === joinName.toLowerCase()) {
      set({ joinError: 'You are unable join using that name' });
      return;
    }

    const matchingParticipant = get().participants.find(
      (participant) => participant.name.trim().toLowerCase() === joinName.toLowerCase()
    );

    if (matchingParticipant && get().pendingRejoinName !== joinName) {
      set({
        pendingRejoinName: board.canRejoinSessions ? joinName : null,
        joinError: board.canRejoinSessions
          ? `Are you sure you want to rejoin as ${joinName}?`
          : `You can't rejoin as ${joinName}. Enter a different name.`,
      });
      return;
    }

    set({ isJoining: true, joinError: null });

    try {
      const participant =
        matchingParticipant ??
        (await wallsApi.addParticipant(board.id, joinName, options?.inviteId, options?.topicKey))
          .participant;

      wallStorage.clearTopicUser(board.id);
      wallStorage.setTopicUser(board.id, participant.id);
      wallStorage.setUserName(participant.name);

      set((state) => ({
        participants: sortedParticipants([
          ...state.participants.filter((candidate) => candidate.id !== participant.id),
          { ...participant, isLive: true },
        ]),
        currentParticipantId: participant.id,
        joinName: '',
        joinError: null,
        pendingRejoinName: null,
      }));
      void import('@walls/services/boardWebsockets').then(({ publishBoardSocketPackage }) => {
        publishBoardSocketPackage({
          action: 'participantAdded',
          topic: board.id,
          participant,
        });
        publishBoardSocketPackage({
          action: 'registerPresent',
          topic: board.id,
          participantId: participant.id,
        });
      });
    } catch (error) {
      set({ joinError: error instanceof Error ? error.message : 'Error joining wall' });
    } finally {
      set({ isJoining: false });
    }
  },

  deleteParticipant: async (boardId, participantId, topicKey) => {
    await wallsApi.deleteParticipants(boardId, [participantId], topicKey);
    get().removeParticipant(participantId);
    void import('@walls/services/boardWebsockets').then(({ publishBoardSocketPackage }) => {
      publishBoardSocketPackage({
        action: 'participantDeleted',
        topic: boardId,
        participantId,
      });
    });
  },

  createGroup: async (boardId, name, participantIds = [], topicKey) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return;
    }

    const { group } = await wallsApi.addGroup(boardId, trimmedName, participantIds, topicKey);

    get().upsertGroup(group);
    publishBoardParticipantPackage({
      action: 'groupAdded',
      topic: boardId,
      group,
    });
  },

  renameGroup: async (boardId, groupId, name, topicKey) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return;
    }

    const { group } = await wallsApi.updateGroup(boardId, groupId, trimmedName, topicKey);

    get().upsertGroup(group);
    publishBoardParticipantPackage({
      action: 'groupUpdated',
      topic: boardId,
      group,
    });
  },

  updateGroup: async (boardId, group, name, participantIds, topicKey) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return;
    }

    const nameChanged = trimmedName !== group.name;
    const participantsChanged = !areParticipantIdsEqual(participantIds, group.participantIds);

    if (!nameChanged && !participantsChanged) {
      return;
    }

    let updatedGroup = group;

    if (nameChanged) {
      updatedGroup = (await wallsApi.updateGroup(boardId, group.id, trimmedName, topicKey)).group;
    }

    if (participantsChanged) {
      updatedGroup = (await wallsApi.setGroupParticipants(boardId, group.id, participantIds, topicKey))
        .group;
    }

    get().upsertGroup(updatedGroup);
    publishBoardParticipantPackage({
      action: 'groupUpdated',
      topic: boardId,
      group: updatedGroup,
    });
  },

  deleteGroup: async (boardId, groupId, topicKey) => {
    await wallsApi.deleteGroup(boardId, groupId, topicKey);

    get().removeGroup(groupId);
    publishBoardParticipantPackage({
      action: 'groupDeleted',
      topic: boardId,
      groupId,
    });
  },

  setGroupParticipants: async (boardId, groupId, participantIds, topicKey) => {
    const { group } = await wallsApi.setGroupParticipants(boardId, groupId, participantIds, topicKey);

    get().upsertGroup(group);
    publishBoardParticipantPackage({
      action: 'groupUpdated',
      topic: boardId,
      group,
    });
  },

  setParticipantLive: (participantId) =>
    set((state) => ({
      participants: sortedParticipants(
        state.participants.map((participant) =>
          participant.id === participantId ? { ...participant, isLive: true } : participant
        )
      ),
    })),

  setParticipantGone: (participantId) =>
    set((state) => ({
      participants: sortedParticipants(
        state.participants.map((participant) =>
          participant.id === participantId ? { ...participant, isLive: false } : participant
        )
      ),
    })),

  resetParticipants: () => set(initialState),
}));

function getOwnerParticipantId(board: WallBoard) {
  const ownerIdMatch = board.ownerId
    ? board.participants.find((participant) => participant.id === board.ownerId)
    : null;

  if (ownerIdMatch) {
    return ownerIdMatch.id;
  }

  const ownerName = board.ownerName?.trim().toLowerCase();

  if (!ownerName) {
    return null;
  }

  return (
    board.participants.find((participant) => participant.name.trim().toLowerCase() === ownerName)
      ?.id ?? null
  );
}

function areParticipantIdsEqual(left: ParticipantId[], right: ParticipantId[]) {
  return (
    left.length === right.length &&
    left.every((participantId, index) => participantId === right[index])
  );
}

function publishBoardParticipantPackage(socketPackage: {
  action: string;
  topic: WallId;
  [key: string]: unknown;
}) {
  void import('@walls/services/boardWebsockets').then(({ publishBoardSocketPackage }) => {
    publishBoardSocketPackage(socketPackage);
  });
}
